import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ArrowLeft, Package, Clock, Truck, CheckCircle, XCircle, CreditCard } from 'lucide-react';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { ORDER_DETAIL } from '@/lib/graphql/queries/customer';
import dayjs from 'dayjs';

type OrderStatus = 'pending' | 'processing' | 'delivered' | 'cancelled';
type PaymentStatus = 'pending' | 'fully-paid' | 'partially-paid' | 'failed' | 'refunded';

interface OrderDetail {
  id: string;
  orderNumber: string;
  total: number;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  createdAt: string;
  remainingPayment: number;
  description: string;
  pickup: boolean;
  paymentData: string;
}

const statusConfig = {
  PENDING: {
    label: 'Хүлээгдэж буй',
    icon: Clock,
    color: 'bg-yellow-100 text-yellow-800',
  },
  PROCESSING: {
    label: 'Боловсруулж буй',
    icon: Package,
    color: 'bg-blue-100 text-blue-800',
  },
  DELIVERED: {
    label: 'Хүргэгдсэн',
    icon: CheckCircle,
    color: 'bg-green-100 text-green-800',
  },
  CANCELLED: {
    label: 'Цуцлагдсан',
    icon: XCircle,
    color: 'bg-red-100 text-red-800',
  },
};

const paymentStatusConfig = {
  PENDING: {
    label: 'Төлөгдөөгүй',
    color: 'bg-yellow-100 text-yellow-800',
  },
  PAID: {
    label: 'Төлөгдсөн',
    color: 'bg-green-100 text-green-800',
  },
  FAILED: {
    label: 'Амжилтгүй',
    color: 'bg-red-100 text-red-800',
  },
  REFUNDED: {
    label: 'Буцаагдсан',
    color: 'bg-gray-100 text-gray-800',
  },
};

const OrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();

  const { data, loading } = useQuery(ORDER_DETAIL, {
    variables: { customerOrderId: id },
    skip: !id,
  });

  const order: OrderDetail | null = data?.customerOrder || null;

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('YYYY-MM-DD');
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('mn-MN').format(price) + '₮';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto">
            <Skeleton className="h-8 w-48 mb-8" />
            <Card>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto text-center py-16">
            <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Захиалга олдсонгүй</h2>
            <p className="text-muted-foreground mb-6">
              Энэ захиалга олдсонгүй эсвэл устгагдсан байна
            </p>
            <Link to="/orders">
              <Button>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Захиалгууд руу буцах
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const statusConf = statusConfig[order.status] || statusConfig.PENDING;
  const paymentConf = paymentStatusConfig[order.paymentStatus] || paymentStatusConfig.PENDING;
  const StatusIcon = statusConf.icon;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <Link 
            to="/orders" 
            className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Захиалгууд руу буцах
          </Link>

          {/* Order Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold">Захиалга #{order.orderNumber}</h1>
              <p className="text-muted-foreground mt-1">{formatDate(order.createdAt)}</p>
            </div>
            <Badge className={statusConf.color}>
              <StatusIcon className="h-4 w-4 mr-1" />
              {statusConf.label}
            </Badge>
          </div>

          {/* Order Details Card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Захиалгын мэдээлэл
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Захиалгын дугаар</p>
                  <p className="font-medium">#{order.orderNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Огноо</p>
                  <p className="font-medium">{formatDate(order.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Хүргэлтийн төрөл</p>
                  <p className="font-medium">{order.pickup ? 'Очиж авах' : 'Хүргэлттэй'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Төлөв</p>
                  <Badge className={statusConf.color}>
                    {statusConf.label}
                  </Badge>
                </div>
              </div>

              {order.description && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Тайлбар</p>
                    <p>{order.description}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Payment Info Card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Төлбөрийн мэдээлэл
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Төлбөрийн төлөв</p>
                  <Badge className={paymentConf.color}>
                    {paymentConf.label}
                  </Badge>
                </div>
                {order.remainingPayment > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground">Үлдэгдэл төлбөр</p>
                    <p className="font-medium text-red-600">{formatPrice(order.remainingPayment)}</p>
                  </div>
                )}
              </div>

              <Separator />

              <div className="flex justify-between items-center">
                <span className="text-lg font-medium">Нийт дүн</span>
                <span className="text-2xl font-bold">{formatPrice(order.total)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-4">
            <Link to="/orders" className="flex-1">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Буцах
              </Button>
            </Link>
            <Link to="/shop" className="flex-1">
              <Button className="w-full">
                Дахин захиалах
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
